import * as React from "react";
import * as _ from "lodash";
import { Select, Form, List, Divider, Message } from "semantic-ui-react";

type ModeOptions = "Remove" | "Add";

export type ModifyLiftParameterBindingsProps = {
  mode: ModeOptions;
  liftParameters: Api.LiftParameter[];
  kceProducts: Api.KceProducts;
  kceVersions: Api.KceVersions;
  updateLiftBindings: (operation: "Add" | "Remove", liftBulkBindings: Api.LiftBulkBindings) => void;
  permission: boolean;
};

type ModifyLiftParameterBindingsState = {
  sourceKceProductId: number;
  sourceKceVersionId: number;
  targetKceProductId: number;
  targetKceVersionId: number;
};

/** ModifyLiftParameterBindings component */
class ModifyLiftParameterBindings extends React.Component<
  ModifyLiftParameterBindingsProps,
  ModifyLiftParameterBindingsState
> {
  state = {
    sourceKceProductId: -1,
    sourceKceVersionId: -1,
    targetKceProductId: -1,
    targetKceVersionId: -1
  };

  componentDidMount(): void {
    const { kceProducts, kceVersions } = this.props;
    const kceVersionList = _.values(kceVersions);
    const kceProductList = _.values(kceProducts);
    if (kceProductList.length > 0) {
      const sourceKceProductId = kceProductList[0].id;
      const targetKceProductId = sourceKceProductId;
      this.setState(() => ({ sourceKceProductId, targetKceProductId }));
    }
    if (kceVersionList.length > 0) {
      const sourceKceVersionId = kceVersionList[0].id;
      const targetKceVersionId = sourceKceVersionId;
      this.setState(() => ({ sourceKceVersionId, targetKceVersionId }));
    }
  }

  render(): JSX.Element {
    const { liftParameters, kceProducts, kceVersions, mode } = this.props;
    const {
      sourceKceProductId,
      sourceKceVersionId,
      targetKceProductId,
      targetKceVersionId
    } = this.state;

    // Creates version dropdown options
    const versionOptions = _.values(kceVersions).map(kceVersion => ({
      key: kceVersion.id,
      text: kceVersion.name,
      value: kceVersion.id
    }));

    // Creates product dropdown options
    const productOptions = _.values(kceProducts).map(kceProduct => ({
      key: kceProduct.id,
      text: kceProduct.productName,
      value: kceProduct.id
    }));

    // Get parameters which contain source binding
    const selectedParameters = liftParameters.filter(
      parameter =>
        !!parameter.liftParameterBindings?.find(
          binding =>
            binding.kceProduct.id === sourceKceProductId &&
            binding.kceVersion.id === sourceKceVersionId
        )
    );
    const selectedParametersCount = selectedParameters.length;

    // Get parameters which contain source binding but not target binding
    const targetParameters = selectedParameters.filter(
      parameter =>
        !parameter.liftParameterBindings?.find(
          binding =>
            binding.kceProduct.id === targetKceProductId &&
            binding.kceVersion.id === targetKceVersionId
        )
    );
    const targetParametersCount = targetParameters.length;

    const targetParametersNames = targetParameters.map(
      parameter => `${parameter.enumName} (code = ${parameter.code})`
    );
    const selectedParametersNames = selectedParameters.map(
      parameter => `${parameter.enumName} (code = ${parameter.code})`
    );

    return (
      <>
        {mode === "Add" ? (
          <Message
            info={true}
            header="Add bindings to parameters"
            list={[
              "Select the source binding in order to select parameters which contain it",
              "Select the new bindings which will be added to the selected parameters",
              "The list shows parameters to which the new binding will be added",
              "Start adding by clicking 'Add binding'"
            ]}
          />
        ) : (
          <Message
            info={true}
            header="Remove bindings from parameters"
            list={[
              "Select the source binding in order to select parameters from which the binding can be removed",
              "The list shows the parameters which contain the binding",
              "Start removing by clicking 'Remove binding'"
            ]}
          />
        )}
        <Divider horizontal={true}>Source binding</Divider>
        <Form.Group>
          <Form>
            <div className="uidropdown">
              <Form.Field
                control={Select}
                label="Product"
                options={productOptions}
                value={sourceKceProductId}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(e: Event, { value }: any) =>
                  this.setState(() => ({ sourceKceProductId: value }))
                }
                disabled={!this.props.permission}
              />
              <Form.Field
                control={Select}
                label="Version"
                options={versionOptions}
                value={sourceKceVersionId}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(e: Event, { value }: any) =>
                  this.setState(() => ({ sourceKceVersionId: value }))
                }
                disabled={!this.props.permission}
              />
            </div>
          </Form>
        </Form.Group>
        {mode === "Remove" ? (
          <>
            <Form.Group className="bindingParameters">
              {selectedParametersCount > 0 ? (
                <>
                  <label>
                    {`Remove selected binding from the following parameters (${selectedParametersCount}):`}
                  </label>
                  <div>
                    <List items={selectedParametersNames} />
                  </div>
                </>
              ) : (
                <>
                  <label>Selected binding not used.</label>
                </>
              )}
            </Form.Group>
            <button
              disabled={selectedParametersCount === 0 || !this.props.permission}
              onClick={() => this.updateBindings("Remove", selectedParameters)}
              className="bindingsActionBtn"
            >
              Remove bindings
            </button>
          </>
        ) : (
          <>
            <Divider horizontal={true}>{"Binding to be added"}</Divider>

            <Form.Group>
              <Form>
                <div className="uidropdown">
                  <Form.Field
                    control={Select}
                    label="Product"
                    options={productOptions}
                    value={targetKceProductId}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onChange={(e: Event, { value }: any) =>
                      this.setState(() => ({ targetKceProductId: value }))
                    }
                    disabled={!this.props.permission}
                  />
                  <Form.Field
                    control={Select}
                    label="Version"
                    options={versionOptions}
                    value={targetKceVersionId}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onChange={(e: Event, { value }: any) =>
                      this.setState(() => ({ targetKceVersionId: value }))
                    }
                    disabled={!this.props.permission}
                  />
                </div>
              </Form>
            </Form.Group>

            <Form.Group className="bindingParameters">
              {targetParametersCount > 0 ? (
                <>
                  <label>
                    {`Add target binding to the following parameters (${targetParametersCount}):`}
                  </label>
                  <div>
                    <List items={targetParametersNames} />
                  </div>
                </>
              ) : (
                <>
                  <label>
                    Binding action is not required because source and target parameters are the
                    same.
                  </label>
                </>
              )}
            </Form.Group>
            <button
              disabled={targetParametersCount === 0 || !this.props.permission}
              onClick={() => this.updateBindings("Add", targetParameters)}
              className="bindingsActionBtn"
            >
              Add bindings
            </button>
          </>
        )}
      </>
    );
  }

  // Add or remove bindings for the given parameters
  private updateBindings = (operation: "Add" | "Remove", parameters: Api.LiftParameter[]): void => {
    if (parameters.length === 0) return;

    const { updateLiftBindings, kceProducts, kceVersions } = this.props;
    const { targetKceProductId, targetKceVersionId, sourceKceProductId, sourceKceVersionId } = this.state;
    const productId = operation === "Add" ? targetKceProductId : sourceKceProductId;
    const versionId = operation === "Add" ? targetKceVersionId : sourceKceVersionId;
    const kceProduct = kceProducts[productId];
    const kceVersion = kceVersions[versionId];
    const domainId = parameters[0].domain.id;

    if (kceProduct && kceVersion) {
      const liftBulkBindings: Api.LiftBulkBindings = {
        domainId,
        targetVersionId: kceVersion.id,
        targetProductId: kceProduct.id,
        parameterIdList: parameters.map(p => p.id)
      };
      updateLiftBindings(operation, liftBulkBindings);
    }
  };
}

export default ModifyLiftParameterBindings;
